import { useState, useEffect, useMemo } from 'react';
import { usePlans, useCreatePlan, useUpdatePlan, useDeletePlan } from '../../hooks/usePlans';
import { useGroups } from '../../hooks/useGroups';
import { useAdditionals } from '../../hooks/useAdditionals';
import { useScoreCoefficients } from '../../hooks/useScoreCoefficient';
import { usePreAdjudicationReasons } from '../../hooks/usePreAdjudicationReasons';
import { useConstructionTypes } from '../../hooks/useConstructionTypes';
import { useLegalEntities } from '../../hooks/useLegalEntities';
import { useFees } from '../../hooks/useFees';
import { useFeeStructures } from '../../hooks/useFeeStructures';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import { usePaymentMethodDetails } from '../../hooks/usePaymentMethodDetails';
import { useBranches } from '../../hooks/useBranches';
import { useFeeTypes } from '../../hooks/useFeeTypes';
import { useUserLogged } from '../../hooks/useUserLogged';
import { INITIAL_PLAN_STATE, ITEMS_PER_PAGE } from '../constants/plansConstants';

/**
 * Hook personalizado para gestionar el estado y lógica de la tabla de planes
 * Sigue los patrones establecidos en el proyecto
 */
export const usePlansTable = () => {
  // Estados locales
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(INITIAL_PLAN_STATE);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [filteredPlans, setFilteredPlans] = useState([]);

  // Datos del usuario
  const { branch, uuid: userUuid } = useUserLogged();

  // Queries principales
  const { data: plansData = [], isLoading: plansLoading } = usePlans();

  // Datos de soporte para el diálogo
  const { data: branches = [], isLoading: branchesLoading } = useBranches();
  const { data: feeTypes = [], isLoading: feeTypesLoading } = useFeeTypes();

  // Precargar datos para las tabs del diálogo
  const { isLoading: groupsLoading } = useGroups();
  const { isLoading: additionalsLoading } = useAdditionals();
  const { isLoading: scoreCoefficientsLoading } = useScoreCoefficients();
  const { isLoading: preAdjReasonsLoading } = usePreAdjudicationReasons();
  const { isLoading: constructionTypesLoading } = useConstructionTypes();
  const { isLoading: legalEntitiesLoading } = useLegalEntities();
  const { isLoading: feesLoading } = useFees();
  const { isLoading: feeStructuresLoading } = useFeeStructures();
  const { isLoading: paymentMethodsLoading } = usePaymentMethods();
  const { isLoading: paymentMethodDetailsLoading } = usePaymentMethodDetails();

  // Mutations
  const createPlanMutation = useCreatePlan();
  const updatePlanMutation = useUpdatePlan();
  const deletePlanMutation = useDeletePlan();

  // Estado de carga global
  const isLoading =
    plansLoading ||
    branchesLoading ||
    feeTypesLoading ||
    groupsLoading ||
    additionalsLoading ||
    scoreCoefficientsLoading ||
    preAdjReasonsLoading ||
    constructionTypesLoading ||
    legalEntitiesLoading ||
    feesLoading ||
    feeStructuresLoading ||
    paymentMethodsLoading ||
    paymentMethodDetailsLoading;

  // Transformar datos de planes con información de sucursales
  const plans = useMemo(() => {
    if (!plansData.length || !branches.length) return [];

    return plansData.map((plan, index) => {
      const planBranch = branches.find((b) => b.uuid === plan.uuid_branch);
      return {
        ...plan,
        id: index,
        branch: planBranch || null,
      };
    });
  }, [plansData, branches]);

  // Filtrar planes según búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setFilteredPlans(plans);
    } else {
      const filtered = plans.filter((plan) =>
        plan.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPlans(filtered);
    }
    setPage(1); // Reset página al filtrar
  }, [plans, searchTerm]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / rowsPerPage));

  const getCurrentPageItems = () => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredPlans.slice(startIndex, endIndex);
  };

  // Handlers
  const handleSearch = (searchText) => {
    setSearchTerm(searchText);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  };

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleOpenDialog = () => {
    setSelectedPlan({
      ...INITIAL_PLAN_STATE,
      branch: branch?.[0] || null,
      uuid_user: userUuid,
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedPlan(INITIAL_PLAN_STATE);
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (planId, planUuid) => {
    try {
      await deletePlanMutation.mutateAsync(planUuid);
      // Los datos se actualizarán automáticamente por TanStack Query
    } catch (error) {
      console.error('Error eliminando plan:', error);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedPlan((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setSelectedPlan((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const planData = {
        name: selectedPlan.name,
        effective_start_date: selectedPlan.effective_start_date,
        installments_number: selectedPlan.installments_number,
        contracts_number: selectedPlan.contracts_number,
        interest_rate: selectedPlan.interest_rate,
        first_installment_amount: selectedPlan.first_installment_amount,
        uuid_branch: selectedPlan.branch?.uuid || selectedPlan.branch?.uuid_branch,
        uuid_user: selectedPlan.uuid_user,
      };

      if (editMode) {
        await updatePlanMutation.mutateAsync({
          id: selectedPlan.uuid,
          data: planData,
        });
      } else {
        await createPlanMutation.mutateAsync(planData);
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error guardando plan:', error);
    }
  };

  return {
    // Estados de datos
    plans: filteredPlans,
    filteredPlans,
    isLoading,
    page,
    searchTerm,

    // Estados de diálogo
    dialogOpen,
    editMode,
    selectedPlan,

    // Datos de soporte
    branches,
    feeTypes,

    // Funciones de manejo
    handleSearch,
    handlePageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleEdit,
    handleDelete,
    handleSave,
    handleInputChange,
    handleSelectChange,

    // Paginación
    getCurrentPageItems,
    totalPages,
    rowsPerPage,
    handleRowsPerPageChange,
  };
};
